<template>
  <v-container fluid class="pa-4">
    <v-layout row wrap>
      <v-flex xs12 v-if="item" class="mb-4">
        <router-link
          :to="{ name: 'DedicationItems' }"
          class="font-weight-bold text--primary"
          style="text-decoration: none;"
        >
          <span style="color:green">Dedication Items</span>
        </router-link>

        <span>
          /
          <v-icon small>mdi-pound</v-icon>
          {{ item.dedicationItemID }} - {{ item.name }}
        </span>
      </v-flex>

      <v-flex cols="12">
        <div v-if="loading" class="px-3 mt-2 py-2">
          <v-progress-linear
            :indeterminate="true"
            color="success"
          ></v-progress-linear>
        </div>

        <!-- Main Card -->
        <v-card v-if="item" class="elevation-2">
          <v-card-title class="text-h5 font-weight-bold primary white--text">
            <v-icon large left color="white">mdi-domain</v-icon>
            <div v-if="!editMode">{{ item.name }} - {{ item.type }}</div>
            <v-layout v-else row wrap>
              <v-flex xs12 sm2>
                <v-text-field
                  dark
                  label="New Name"
                  v-model="newName"
                  type="text"
                  :rules="[(v) => !!v || 'Required.']"
                ></v-text-field>
              </v-flex>
              <v-flex xs12 sm1>
                <v-select
                  v-if="newType != 'other'"
                  dark
                  label="זכות *"
                  :items="getTypeListValue()"
                  v-model="newType"
                ></v-select>
                <v-text-field
                  v-else
                  label="זכות *"
                  v-model="newCustomType"
                  :rules="[(v) => !!v || 'Required.']"
                  required
                ></v-text-field>
              </v-flex>
            </v-layout>
          </v-card-title>

          <v-card-text class="pt-6">
            <v-layout row wrap>
              <v-flex xs12 md7>
                <v-list two-line class="bg-transparent">
                  <!-- Site -->
                  <v-list-tile class="px-0">
                    <v-icon color="blue-grey" class="mr-4"
                      >mdi-office-building</v-icon
                    >
                    <span class="font-weight-medium mr-2">Site:</span>
                    <div v-if="!editMode">
                      <span class="text-h6">{{ buildingName }}</span>
                    </div>
                    <div v-else class="d-flex align-center ml-4">
                      <v-select
                        label="Select Site *"
                        :items="buildings"
                        item-text="description"
                        item-value="code"
                        v-model="newBuildingCode"
                        class="mr-4"
                        style="max-width: 300px;"
                      ></v-select>
                    </div>
                  </v-list-tile>

                  <v-divider></v-divider>

                  <!-- Created By -->
                  <v-list-tile class="px-0">
                    <v-icon color="blue-grey" class="mr-4">mdi-account</v-icon>
                    <span class="font-weight-medium mr-2">Created By:</span>
                    <span class="text-h6">{{ item.user }}</span>
                  </v-list-tile>

                  <v-divider></v-divider>

                  <!-- Suggested Price -->
                  <v-list-tile class="px-0">
                    <v-icon color="green" class="mr-4">mdi-currency-usd</v-icon>
                    <span class="font-weight-medium mr-2"
                      >Suggested Price:</span
                    >
                    <div v-if="!editMode">
                      <span
                        class="text-h5 font-weight-bold green--text text--darken-2"
                      >
                        {{ formatPrice(item.suggestedPrice) }}
                      </span>
                    </div>
                    <div class="d-flex align-center ml-4" v-else>
                      <v-text-field
                        label="New Price"
                        v-model.number="newPrice"
                        type="number"
                        prefix="$"
                        :rules="[(v) => v >= 0 || 'Price must be positive']"
                      ></v-text-field>
                    </div>
                  </v-list-tile>

                  <v-divider v-if="item.sortOrder !== null"></v-divider>

                  <!-- Sort Order -->
                  <v-list-tile class="px-0" v-if="item.sortOrder !== null">
                    <v-icon color="blue-grey" class="mr-4">mdi-sort</v-icon>
                    <span class="font-weight-medium mr-2">Sort Order:</span>
                    <span class="text-h6">{{ item.sortOrder }}</span>
                  </v-list-tile>

                  <v-divider></v-divider>

                  <!-- Status -->
                  <v-list-tile class="px-0">
                    <v-icon :color="getStatusColor()" class="mr-4"
                      >mdi-circle</v-icon
                    >
                    <span class="font-weight-medium mr-2">Status:</span>
                    <div v-if="!editMode">
                      <span>{{ getStatusText() }}</span>
                    </div>
                    <div v-else class="d-flex align-center ml-4">
                      <v-select
                        label="Select Status"
                        :items="statusList"
                        v-model="newStatus"
                        class="mr-4"
                        style="max-width: 300px;"
                      ></v-select>
                    </div>
                  </v-list-tile>
                </v-list>
              </v-flex>

              <v-flex xs12 md5>
                <h3 class="mb-3 text-h6 font-weight-medium primary--text">
                  <v-icon small left color="primary">mdi-text-box</v-icon>
                  Description
                </h3>
                <div v-if="!editMode">
                  <v-card
                    v-if="item.description"
                    flat
                    class="pa-4 description-card"
                    style="background: #f8f9fa; border-left: 4px solid #1976d2; border-radius: 0 8px 8px 0;"
                  >
                    <div
                      class="grey--text text--darken-3 body-1"
                      style="line-height: 1.7; white-space: pre-wrap;"
                      v-html="item.description"
                    ></div>
                  </v-card>

                  <v-card
                    v-else
                    flat
                    class="pa-4 text-center grey--text text--lighten-1"
                    style="background: #f5f5f5; border: 1px dashed #ccc; border-radius: 8px;"
                  >
                    <v-icon small color="grey lighten-1" class="mb-2"
                      >mdi-information</v-icon
                    >
                    <div class="caption">No description provided.</div>
                  </v-card>
                </div>
                <div v-else>
                  <v-textarea
                    label="Edit Description"
                    v-model="description"
                    type="text"
                  ></v-textarea>
                </div>
              </v-flex>
            </v-layout>
            <v-card-actions class="pa-4">
              <v-spacer />
              <div v-if="!editMode">
                <v-btn color="success" @click="editMode = true">
                  <v-icon left>mdi-pencil</v-icon>
                  Edit Item
                </v-btn>
              </div>
              <div v-else>
                <v-btn color="default" dark @click="reset()">
                  Cancel
                </v-btn>
                <v-btn color="success" @click="editItem">
                  <v-icon left>mdi-check</v-icon>
                  Save
                </v-btn>
              </div>
            </v-card-actions>
            <v-layout row wrap class="mt-8">
              <v-flex xs12>
                <h3 class="mb-2">Documents ({{ item.documents.length }})</h3>
                <v-btn
                  class="mb-4"
                  small
                  color="success"
                  @click="uploadFiles.dialog = true"
                  ><v-icon dark left>mdi-cloud-upload</v-icon> Upload
                  files</v-btn
                >
                <div v-if="item.documents.length === 0" class="grey--text">
                  No documents attached.
                </div>

                <div v-else class="mt-6">
                  <v-layout row wrap>
                    <v-card
                      v-for="doc in [...item.documents].sort(
                        (a, b) => a.documentID - b.documentID
                      )"
                      :key="doc.fileURL"
                      class="pa-3 mb-4 mx-2"
                      style="width: 24%; min-width: 280px;"
                      elevation="2"
                    >
                      <div class="mb-2">
                        <div class="d-flex align-center justify-space-between">
                          <div
                            class="text-subtitle-1 font-weight-medium text-truncate"
                            style="max-width: 70%;"
                          >
                            File name: {{ doc.fileName }}
                          </div>
                        </div>
                        <div
                          class="d-flex align-center justify-space-between mt-2"
                        >
                          <div
                            class="text-subtitle-1 font-weight-medium text-truncate"
                            style="max-width: 70%;"
                          >
                            Description: {{ doc.fileDescription }}
                          </div>
                        </div>
                      </div>
                      <v-layout>
                        <!--  -->
                        <v-spacer></v-spacer>
                        <v-btn
                          small
                          color="success"
                          class="mr-1"
                          style="height: 28px;"
                          @click="view(doc)"
                        >
                          <v-icon class="mr-2">mdi-open-in-new</v-icon>
                          View</v-btn
                        >
                        <v-btn
                          small
                          color="success"
                          style="height: 28px;"
                          @click="openDeleteFileDialog(doc)"
                          >Delete</v-btn
                        >
                      </v-layout>
                      <div
                        style="height: 400px; overflow-y: auto; border: 1px solid #e0e0e0; border-radius: 4px;"
                      >
                        <pdf
                          v-if="isPdf(doc.fileURL)"
                          :src="doc.fileURL"
                          style="width: 100%;"
                        ></pdf>
                        <img
                          v-else
                          :src="doc.fileURL"
                          style="width: 100%; height: auto; display: block;"
                        />
                      </div>
                    </v-card>
                  </v-layout>
                </div>
              </v-flex>
            </v-layout>
          </v-card-text>
        </v-card>
      </v-flex>
    </v-layout>
    <v-dialog persistent v-model="uploadFiles.dialog" max-width="400">
      <v-card class="pa-3">
        <v-card-title class="headline pb-2 pl-0 pt-0"
          >Upload Documents</v-card-title
        >

        <v-btn
          :disabeld="uploadFiles.loading"
          small
          outline
          color="success"
          :loading="uploadFiles.loading"
          @click="pickFile()"
          >{{ !uploadFiles.URL ? "Upload" : "re-Upload" }}</v-btn
        >

        <div v-show="!uploadFiles.URL" class="dropArea" ref="dropArea">
          Drag and drop a file here
        </div>

        <input
          type="file"
          style="display: none"
          ref="files"
          accept=".pdf, image/*"
          @change="onFilePicked($event)"
        />

        <div
          style="height: 600px; overflow-y: auto;"
          class="mt-3"
          v-if="uploadFiles.URL"
        >
          <pdf
            style="max-height: 500px !important; width: 100%;"
            :src="uploadFiles.URL"
            v-if="isPdf(uploadFiles.URL)"
          ></pdf>
          <img
            style="max-height: 500px !important; width: 100%;"
            v-if="!isPdf(uploadFiles.URL)"
            :src="uploadFiles.URL"
          />
        </div>
        <div v-if="uploadFiles.URL">
          <v-text-field label="Name" v-model="uploadFiles.name"> </v-text-field>
          <v-text-field label="Description" v-model="uploadFiles.description">
          </v-text-field>
        </div>
        <v-card-actions class="mr-0">
          <v-spacer></v-spacer>
          <v-btn color="default" dark @click="resetUpload()">Cancel</v-btn>
          <v-btn
            :loading="uploadFiles.loading"
            color="success"
            @click="saveFiles()"
            >Save</v-btn
          >
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-dialog persistent v-model="deleteFileDialog" max-width="400">
      <v-card class="pa-2">
        <v-card-title class="text-warning headline">Are You Sure?</v-card-title>
        <v-card-text class="mb-3 py-0 text-truncate">
          You will now delete: {{ selectedFile.fileName }}
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="default" dark @click="resetDelete()">Close</v-btn>
          <v-btn :loading="deleting" color="success" @click="deleteFile()"
            >Delete</v-btn
          >
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import { formatCurrency } from "../../utils/helpers.js";
import { truncate } from "../../utils/helpers.js";
import { loadOptions } from "../../utils/smLookup.js";
import pdf from "vue-pdf";

export default {
  components: {
    pdf,
  },
  data: () => ({
    loading: true,
    error: false,
    item: null,
    buildings: [],
    typeList: [],
    buildingCode: null,
    buildingName: "",
    editMode: false,
    newBuildingCode: "",
    newPrice: null,
    newStatus: "",
    newType: "",
    newCustomType: "",
    statusList: [],
    newName: "",
    deleteFileDialog: false,
    selectedFile: "",
    deleting: false,
    description: "",

    uploadFiles: {
      dialog: false,
      loading: false,
      name: "",
      type: "",
      URL: "",
      updating: false,
      description: "",
    },
  }),

  async created() {
    await this.loadItem();
    this.loadStatusList();
    await this.loadTypeList();
    this.newBuildingCode = this.item.buildingCode;
    this.newPrice = this.item.suggestedPrice;
    this.newStatus = this.item.status;
    this.newType = this.item.type;
    this.newName = this.item.name;
    this.description = this.item.description;

    const dropArea = this.$refs.dropArea;
    ["dragenter", "dragover", "dragleave", "drop"].forEach((eventName) => {
      dropArea.addEventListener(eventName, this.preventDefaults, false);
    });

    ["dragenter", "dragover"].forEach((eventName) => {
      dropArea.addEventListener(eventName, this.highlight, false);
    });

    ["dragleave", "drop"].forEach((eventName) => {
      dropArea.addEventListener(eventName, this.unhighlight, false);
    });

    ["click"].forEach((eventName) => {
      dropArea.addEventListener(eventName, this.pickFile, false);
    });

    dropArea.addEventListener("drop", this.handleDrop, false);
  },
  methods: {
    async loadItem() {
      try {
        const response = await this.$http.get(
          `api/dedicationitems/GetItemByID?id=${this.$route.params.id}`
        );
        this.item = response.data || response;
        await this.getBuildings();
      } catch (e) {
        console.error(e);
        this.error = true;
        setTimeout(() => {
          this.$router.push("/dediPro/dedication-items");
        }, 2000);
      } finally {
        this.loading = false;
      }
    },
    async loadStatusList() {
      var options = await loadOptions("DedicationItemStatus");
      this.statusList = options
        .sort((a, b) => a.sortOrder - b.sortOrder)
        .map((c) => c.value);
    },
    formatPrice(price) {
      return formatCurrency(price);
    },

    openDocument(doc) {
      if (doc.url) window.open(doc.url, "_blank");
    },

    getStatusColor() {
      if (!this.item || !this.item.status) return "grey";
      switch (this.item.status) {
        case "Available":
          return "green";
        case "Sold out":
          return "orange";
        case "Unavailable":
          return "red";
        default:
          return "grey";
      }
    },

    getStatusText() {
      if (!this.item || !this.item.status) return "Unknown";
      return this.item.status;
    },

    async getBuildings() {
      try {
        const response = await this.$http.get(
          `api/dedicationitems/getBuildings`
        );
        this.buildings = response;
        this.buildingName =
          response.find((b) => b.code === this.item.buildingCode).description ||
          null;
        if (this.buildingName.split("")[1] === " ") {
          this.buildingName =
            this.buildingName.slice(0, 1) + this.buildingName.slice(2);
        }
      } catch (error) {}
    },
    async loadTypeList() {
      var options = await loadOptions("DedicationItemType");
      this.typeList = options
        .sort((a, b) => a.sortOrder - b.sortOrder)
        .map((c) => {
          c.selected = false;
          return c;
        });
    },
    async editItem() {
      if (
        this.newPrice < 0 ||
        !this.newName ||
        (this.newType == "other" && !this.newCustomType)
      ) {
        return;
      }
      this.processing = true;
      try {
        const payload = {
          DedicationItemID: this.item.dedicationItemID,
          BuildingCode: this.newBuildingCode,
          Name: this.newName,
          Type: this.newCustomType ? this.newCustomType : this.newType,
          SuggestedPrice: this.newPrice,
          Description: this.description,
          Status: this.newStatus,
        };
        const response = await this.$http.put(
          "api/dedicationitems/UpdateItem",
          payload
        );
        await this.loadItem();
        this.reset();
      } catch {
      } finally {
        this.processing = false;
      }
    },
    reset() {
      this.editMode = false;
      this.newBuildingCode = this.item.buildingCode;
      this.newPrice = this.item.suggestedPrice;
      this.newStatus = this.item.status;
      this.newType = this.item.type;
      this.newCustomType = "";
      this.newName = this.item.name;
    },
    pickFile() {
      this.$refs.files.click();
    },

    async onFilePicked(e) {
      this.uploadFiles.loading = true;
      const files = e.target.files;

      if (!files) return;

      try {
        var formData = new FormData();

        this.files = files;
        formData.append("upload_file", this.files[0]);

        const response = await this.$http.post(
          "api/adminPortal/UploadFile?",
          formData,
          {
            headers: {
              "Content-Type": "multipart/form-data",
            },
          }
        );

        const fileName = this.files[0].name;
        const lastDotIndex = fileName.lastIndexOf(".");
        this.uploadFiles.name =
          lastDotIndex > 0 ? fileName.substring(0, lastDotIndex) : fileName;

        this.uploadFiles.type = this.files[0].type;
        this.uploadFiles.URL = response.url;
        this.uploadFiles.dialog = true;
      } catch (e) {
      } finally {
        this.uploadFiles.loading = false;
      }
    },

    async saveFiles() {
      if (!this.uploadFiles.URL) return;
      this.uploadFiles.loading = true;
      try {
        await this.$http.post("api/dedicationitems/UpdateAttachments", {
          DedicationItemID: this.item.dedicationItemID,
          Add: [
            {
              FileName: this.uploadFiles.name,
              FileType: this.uploadFiles.type,
              FileURL: this.uploadFiles.URL,
              User: this.$store.state.authentication.user.toLowerCase(),
              FileDescription: this.uploadFiles.description,
            },
          ],
        });
      } catch (error) {
      } finally {
        this.uploadFiles.loading = false;
        this.resetUpload();
        await this.loadItem();
      }
    },
    resetUpload() {
      this.uploadFiles = {
        dialog: false,
        loading: false,
        name: "",
        type: "",
        URL: "",
        updating: false,
      };
    },

    isPdf(url) {
      return url.toLowerCase().includes("pdf");
    },
    handleDrop(e) {
      this.preventDefaults(e);
      const files = e.dataTransfer.files;
      if (files.length === 0 || this.uploadFiles.URL) return;

      const fakeEvent = { target: { files } };
      this.onFilePicked(fakeEvent);
    },

    highlight(e) {
      e.preventDefault();
      e.target.classList.add("highlight");
    },

    unhighlight(e) {
      e.target.classList.remove("highlight");
    },

    preventDefaults(e) {
      e.preventDefault();
      e.stopPropagation();
    },
    view(file) {
      window.open(file.fileURL);
    },
    openDeleteFileDialog(file) {
      this.deleteFileDialog = true;
      this.selectedFile = file;
    },
    async deleteFile() {
      this.deleting = true;
      try {
        await this.$http.post("api/dedicationitems/UpdateAttachments", {
          DedicationItemID: this.item.dedicationItemID,
          RemoveDocumentIDs: [this.selectedFile.documentID],
        });
      } catch (error) {
      } finally {
        this.loadItem();
        this.resetDelete();
      }
    },
    resetDelete() {
      this.deleteFileDialog = false;
      this.selectedFile = "";
      this.deleting = false;
    },
    getTypeListValue() {
      const typeValue = this.typeList.map((i) => i.value);
      typeValue.push("other");
      return typeValue;
    },
  },
};
</script>

<style scoped>
.v-card-title {
  word-break: break-word;
}

.dropArea {
  border: 2px dashed #ccc;
  width: 100%;
  height: 150px;
  text-align: center;
  padding: 20px;
  cursor: pointer;
}
.highlight {
  border-color: #fc0;
}
</style>
